import type { IndustryCaseRecommendationData } from '@/components/MessageList/IndustryCaseRecommendationMessage/const';
import { parseIndustryCaseRecommendation } from '@/components/MessageList/IndustryCaseRecommendationMessage/helper';
import { fixMalformedJSON } from '@/utils/json';

export interface ProjectLaunchOrderData {
  projectName: string;
  positioning: string;
  visualDirection: string;
  referenceImageUrl?: string;
  websiteLanguage: string;
  targetAudience: string;
  primaryGoal: string;
  pages: string[];
  userLanguage?: string;
  industryCaseRecommendation?: IndustryCaseRecommendationData;
  recommendationLatencyMs?: number;
  recommendationCandidateCount?: number;
  recommendationStatus?: string;
}

function parseString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is string => typeof item === 'string')
    .map(item => item.trim())
    .filter(Boolean);
}

export function parseProjectLaunchOrderData(content: string): ProjectLaunchOrderData | null {
  const trimmed = content.trim();
  let parsed: Record<string, unknown>;

  try {
    parsed = JSON.parse(trimmed) as Record<string, unknown>;
  } catch {
    try {
      parsed = JSON.parse(fixMalformedJSON(trimmed)) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const data: ProjectLaunchOrderData = {
    industryCaseRecommendation: parseIndustryCaseRecommendation(parsed.industryCaseRecommendation) ?? undefined,
    recommendationLatencyMs: typeof parsed.recommendationLatencyMs === 'number' ? parsed.recommendationLatencyMs : undefined,
    recommendationCandidateCount: typeof parsed.recommendationCandidateCount === 'number'
      ? parsed.recommendationCandidateCount
      : undefined,
    recommendationStatus: parseString(parsed.recommendationStatus) || undefined,
    projectName: parseString(parsed.projectName),
    positioning: parseString(parsed.positioning),
    visualDirection: parseString(parsed.visualDirection),
    referenceImageUrl: parseString(parsed.referenceImageUrl) || undefined,
    websiteLanguage: parseString(parsed.websiteLanguage),
    targetAudience: parseString(parsed.targetAudience),
    primaryGoal: parseString(parsed.primaryGoal),
    pages: parseStringArray(parsed.pages),
    userLanguage: parseString(parsed.userLanguage) || undefined,
  };

  if (!data.projectName && !data.positioning) {
    return null;
  }

  return data;
}

export function extractProjectLaunchOrder(content: string): ProjectLaunchOrderData | null {
  const match = content.match(
    /<wegic[-_]project[-_]launch\b[^>]*>([\s\S]*?)<\/wegic[-_]project[-_]launch>/i,
  );

  return match?.[1] ? parseProjectLaunchOrderData(match[1]) : null;
}
